import React, { MouseEventHandler, useContext, useEffect, useState } from 'react';
import OutlineButton from 'Components/Common/components/Button/OutlineButton';
import ModalBody from 'Components/Common/Modal/ModalBody';
import ModalHeader from 'Components/Common/Modal/ModalHeader';
import ModalContainer from 'Components/Common/Modal/ModalContainer';
import ModalFooter from 'Components/Common/Modal/ModalFooter';
import { ProductsContext } from '../ProductsContext';
import { getParentById } from 'api/Parent';
import { toast } from 'react-toastify';
import { TAttribute, TParent, TVariantAttribute } from 'types/Pages/ProductDelta';
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';
import FillButton from 'Components/Common/components/Button/FillButton';

interface DialogProps {
    isOpen:boolean,
    onClose: () => void;
    handleSubmit: (search: string, disabledAttributes: string[]) => void,
    // loading: boolean,
    heading: string
    buttonLabel: string
}

const CreateEditParentDialog: React.FC<DialogProps> = ({ 
  isOpen,
  onClose, 
  heading,
  handleSubmit,
  buttonLabel
}) => {
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState<boolean>(false)
  const [disabledAttributes, setDisabledAttributes] = useState<string[]>([])

  const {
    editParent,
    attributes
  } = useContext(ProductsContext)

  const _handleSubmit: React.MouseEventHandler<HTMLButtonElement> = async (e) =>{
    e.preventDefault()
    setLoading(true)
    await handleSubmit(search, disabledAttributes)
    setLoading(false)
  }

  useEffect(() => {
    setSearch("")
    setDisabledAttributes([])
    if(!editParent) return
    setLoading(true)
    // fetch parent data
    getParentById(editParent.id)
    .then((parent) => {
      setSearch(parent.data.parent.name)
      const activeAttributes = parent.data.attributes.map((attr: {name: string, uid: string}) => attr.uid)

      const dis_attrs = attributes
      ?.filter((attribute: TAttribute) => attribute.variant_defining)
      .filter((attr: TAttribute) => !activeAttributes.includes(attr.id))
      .map((attr: TAttribute) => attr.id)
  
      setDisabledAttributes(dis_attrs ?? [])
    })
    .catch((err) => {
      toast.error(err.message)
    })
    .finally (() => setLoading(false))
  }, [editParent, isOpen])

  const handleToggleButton = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    if(e.target.checked)
      return setDisabledAttributes((prev) => {
        const next = [ ...prev ]
        const index = next.indexOf(id)
        next.splice(index, 1)
        return next
      })

    setDisabledAttributes((prev) => {
      const index = prev.indexOf(id)
      if(index !== -1) return prev
      return [...prev, id]
    })
  }

  const handleClose: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    setSearch("")
    setDisabledAttributes([])
    onClose()
  }

  const variant_defining_atts = attributes
            ?.filter((attribute: TAttribute) => attribute.variant_defining)

  return (
    <ModalContainer isOpen={isOpen} setOpen={handleClose} width='968px'>
      <ModalHeader>
          <h2 className={`tw-text-sm tw-font-semibold tw-my-3  tw-px-3.5`}>{heading}</h2>
      </ModalHeader>
      <ModalBody>
        <div className={`tw-my-3 tw-px-3.5`}>
            <label className={`tw-font-normal tw-w-full tw-text-sm `}>Enter Parent Name</label>
            <input  onChange={(e) => setSearch(e.target.value)} value={search} type='text' className={`tw-w-full tw-mt-1 tw-border tw-px-[10px] tw-gap-2.5 tw-py-2 tw-bg-white tw-rounded focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-gray-300`} placeholder='Enter Parent Name/UID' />
        </div>

        {
          variant_defining_atts && variant_defining_atts?.length>0 && 
          <div className="tw-px-3.5">
          <p className="tw-text-xs tw-text-lightslategray tw-font-bold">Variant Defining Attributes</p>
          <div className="tw-grid tw-grid-cols-5 tw-flex-wrap tw-gap-2 mt-2">
            {
              variant_defining_atts
              .map((attr: TAttribute, key: number) => (
                <div key={key} className="tw-flex tw-flex-col tw-space-y-1.5">
                  <label>{attr.name}</label>
                  <ToggleButton 
                    name={attr.name} 
                    value={!disabledAttributes.includes(attr.id)} 
                    onChange={(e) => handleToggleButton(e, attr.id)}/>
                </div>
              ))
            }
          </div>
          </div>
        }
      </ModalBody>
      <ModalFooter>
        <div className={`tw-my-4 tw-flex tw-gap-2 tw-justify-end tw-mt-6 tw-px-3.5`}>
            <OutlineButton onClick={handleClose}>Cancel</OutlineButton>
            <FillButton disabled={loading} onClick={_handleSubmit} >{buttonLabel}</FillButton>
        </div>
      </ModalFooter>
    </ModalContainer>
  );
};

export default CreateEditParentDialog;
